/**
 * The sounds of Poisson rain, synthesized with Web Audio so no files ship:
 * a soft rain hiss, a hang drum note for each drop, birdsong now and then,
 * and thunder. The weather (hiss, thunder and birds) and the notes each have
 * their own volume, under one master gain, so stopping is instant. Start it
 * only from a click: browsers allow audio only after one.
 */
import {DEFAULT_RAIN_VOLUME, type RainVolume, volumeGain} from './rainVolume';

/** Overall volume, kept low: this plays over the whole site. */
const MASTER_GAIN = 0.5;

/** The rain hiss's volume. */
const RAIN_GAIN = 0.05;

/** A drop note's peak volume. */
const DROP_GAIN = 0.11;

/**
 * A hang drum note's partials as [multiple of the note, volume, seconds to
 * fade, detune in cents]. A handpan's notes are tuned so the octave and the
 * twelfth ring with the note; the second voice of the note, a few cents
 * sharp, beats slowly against the first and gives the shimmer.
 */
const HANG_PARTIALS: readonly (readonly [number, number, number, number])[] = [
  [1, 1, 2.6, 0],
  [1, 0.3, 2.2, 3],
  [2, 0.34, 1.5, 5],
  [3, 0.09, 0.7, -4],
];

/** How much of the drop notes goes to the room's echo. */
const ROOM_GAIN = 0.32;

/** The room's echo length, in seconds. */
const ROOM_SECONDS = 2.4;

/** Thunder's peak volume, for a strike close by. */
const THUNDER_GAIN = 0.6;

/** Birdsong, as chirps of [pitch in Hz, start in seconds]. */
const BIRD_SONGS: readonly (readonly (readonly [number, number])[])[] = [
  [
    [2349, 0],
    [2637, 0.09],
    [2349, 0.18],
    [3136, 0.34],
  ],
  [
    [2093, 0],
    [2093, 0.12],
    [2637, 0.24],
    [3136, 0.33],
    [2637, 0.42],
  ],
  [
    [3136, 0],
    [2794, 0.1],
    [2637, 0.2],
    [2349, 0.3],
  ],
];

/** The running sounds of one shower. */
export interface RainAudio {
  /**
   * Plays one drop's note on the hang drum. Notes are booked on the audio
   * clock, so they play on time even in a background tab.
   *
   * @param params.frequency - The note's pitch in Hz.
   * @param params.delaySec - Seconds from now to play it. Omitted, or in
   *     the past, means now.
   */
  playDrop: (params: {frequency: number; delaySec?: number}) => void;
  /**
   * Plays a short birdsong, picked at random.
   *
   * @param params.delaySec - Seconds from now to sing it. Omitted, or in
   *     the past, means now.
   */
  playBird: (params?: {delaySec?: number}) => void;
  /**
   * Plays a roll of thunder: a crack and a rumble for a strike close by,
   * only a softer, lower rumble for one far off.
   *
   * @param params.distance - How far off the strike is, in [0, 1].
   * @param params.delaySec - Seconds from now to start it. Omitted, or in
   *     the past, means now.
   */
  playThunder: (params: {distance: number; delaySec?: number}) => void;
  /**
   * Sets the weather's and the notes' volumes, gliding to them so nothing
   * clicks.
   *
   * @param volume - The new levels.
   */
  setVolume: (volume: RainVolume) => void;
  /** Fades everything out and releases the audio; later calls do nothing. */
  stop: () => void;
}

/** A shower that makes no sound, where Web Audio is unavailable. */
const SILENT: RainAudio = {
  playDrop: () => {},
  playBird: () => {},
  playThunder: () => {},
  setVolume: () => {},
  stop: () => {},
};

/**
 * A synthetic room: stereo noise fading away, for a ConvolverNode, so the
 * notes ring in a space rather than stop dead.
 */
function roomImpulse(context: AudioContext): AudioBuffer {
  const length = Math.floor(context.sampleRate * ROOM_SECONDS);
  const buffer = context.createBuffer(2, length, context.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const samples = buffer.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      samples[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
    }
  }
  return buffer;
}

/**
 * Four seconds of brown noise (white noise summed up, so most of its power
 * is low), for thunder.
 */
function rumbleBuffer(context: AudioContext): AudioBuffer {
  const buffer = context.createBuffer(
    1,
    context.sampleRate * 4,
    context.sampleRate,
  );
  const samples = buffer.getChannelData(0);
  let level = 0;
  for (let i = 0; i < samples.length; i += 1) {
    level = (level + 0.02 * (Math.random() * 2 - 1)) / 1.02;
    samples[i] = level * 3.5;
  }
  return buffer;
}

/** Two seconds of white noise, looped for the rain. */
function noiseBuffer(context: AudioContext): AudioBuffer {
  const buffer = context.createBuffer(
    1,
    context.sampleRate * 2,
    context.sampleRate,
  );
  const samples = buffer.getChannelData(0);
  for (let i = 0; i < samples.length; i += 1) {
    samples[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/**
 * Starts the sounds of a shower: the rain hiss fades in at once; drops and
 * birds play when asked.
 *
 * @param params.volume - The levels to start at. Omitted means
 *     {@link DEFAULT_RAIN_VOLUME}.
 * @return Controls for the shower's sounds; silent ones where Web Audio is
 *     unavailable.
 * @throws Never; audio failures are logged at debug level.
 */
export function startRainAudio({
  volume = DEFAULT_RAIN_VOLUME,
}: {volume?: RainVolume} = {}): RainAudio {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') {
    return SILENT;
  }
  try {
    const context = new AudioContext();
    void context.resume();
    const master = context.createGain();
    master.gain.value = MASTER_GAIN;
    // Keeps a burst of overlapping notes from clipping.
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -12;
    limiter.ratio.value = 6;
    master.connect(limiter).connect(context.destination);

    const weather = context.createGain();
    weather.gain.value = volumeGain(volume.rain);
    weather.connect(master);
    const notes = context.createGain();
    notes.gain.value = volumeGain(volume.drops);
    notes.connect(master);
    const room = context.createConvolver();
    room.buffer = roomImpulse(context);
    const roomGain = context.createGain();
    roomGain.gain.value = ROOM_GAIN;
    notes.connect(room).connect(roomGain).connect(master);
    const noise = noiseBuffer(context);
    const rumble = rumbleBuffer(context);
    let isStopped = false;

    const rain = context.createBufferSource();
    rain.buffer = noise;
    rain.loop = true;
    const highpass = context.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 500;
    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 5000;
    const rainGain = context.createGain();
    rainGain.gain.setValueAtTime(0.0001, context.currentTime);
    rainGain.gain.exponentialRampToValueAtTime(
      RAIN_GAIN,
      context.currentTime + 2,
    );
    rain.connect(highpass).connect(lowpass).connect(rainGain).connect(weather);
    rain.start();

    /**
     * One hang drum note: a soft hand's attack, partials ringing out at
     * their own speeds, and the faint thump of the touch.
     */
    const hang = ({
      frequency,
      delaySec = 0,
    }: {
      frequency: number;
      delaySec?: number;
    }) => {
      const start = context.currentTime + Math.max(0, delaySec);
      for (const [multiple, volume, fadeSec, cents] of HANG_PARTIALS) {
        const oscillator = context.createOscillator();
        const envelope = context.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency * multiple, start);
        oscillator.detune.setValueAtTime(cents, start);
        envelope.gain.setValueAtTime(0.0001, start);
        envelope.gain.linearRampToValueAtTime(
          DROP_GAIN * volume,
          start + 0.012,
        );
        envelope.gain.exponentialRampToValueAtTime(0.0001, start + fadeSec);
        oscillator.connect(envelope).connect(notes);
        oscillator.start(start);
        oscillator.stop(start + fadeSec + 0.05);
      }
      const touch = context.createBufferSource();
      touch.buffer = noise;
      const muffle = context.createBiquadFilter();
      muffle.type = 'lowpass';
      muffle.frequency.value = frequency * 2;
      const touchGain = context.createGain();
      touchGain.gain.setValueAtTime(DROP_GAIN * 0.5, start);
      touchGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.05);
      touch.connect(muffle).connect(touchGain).connect(notes);
      touch.start(start, Math.random());
      touch.stop(start + 0.06);
    };

    /** One short sine chirp: a quick attack and an exponential decay. */
    const chirp = ({
      frequency,
      gain,
      startSec,
      durationSec,
    }: {
      frequency: number;
      gain: number;
      startSec: number;
      durationSec: number;
    }) => {
      const start = context.currentTime + startSec;
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, start);
      envelope.gain.setValueAtTime(0.0001, start);
      envelope.gain.exponentialRampToValueAtTime(gain, start + 0.008);
      envelope.gain.exponentialRampToValueAtTime(0.0001, start + durationSec);
      oscillator.connect(envelope).connect(weather);
      oscillator.start(start);
      oscillator.stop(start + durationSec + 0.05);
    };

    return {
      playDrop: hang,
      playThunder: ({distance, delaySec = 0}) => {
        const start = context.currentTime + Math.max(0, delaySec);
        const lengthSec = 3 + 3 * distance + Math.random() * 1.5;
        const peak = THUNDER_GAIN * (1 - 0.55 * distance);
        const source = context.createBufferSource();
        source.buffer = rumble;
        source.loop = true;
        const muffle = context.createBiquadFilter();
        muffle.type = 'lowpass';
        muffle.frequency.value = 420 - 260 * distance;
        const body = context.createBiquadFilter();
        body.type = 'peaking';
        body.frequency.value = 110;
        body.gain.value = 6;
        const envelope = context.createGain();
        envelope.gain.setValueAtTime(0.0001, start);
        envelope.gain.linearRampToValueAtTime(
          peak,
          start + 0.06 + 0.5 * distance,
        );
        // It rolls: a few swells, each a little weaker than the last.
        const swells = 3 + Math.floor(Math.random() * 4);
        for (let swell = 1; swell <= swells; swell += 1) {
          envelope.gain.linearRampToValueAtTime(
            peak * (0.3 + 0.7 * Math.random()) * (1 - swell / (swells + 1)),
            start + (swell / swells) * lengthSec * 0.8,
          );
        }
        envelope.gain.exponentialRampToValueAtTime(0.0001, start + lengthSec);
        source.connect(muffle).connect(body).connect(envelope).connect(weather);
        source.start(start, Math.random() * 2);
        source.stop(start + lengthSec + 0.1);
        if (distance >= 0.4) {
          return;
        }
        const crack = context.createBufferSource();
        crack.buffer = noise;
        const bright = context.createBiquadFilter();
        bright.type = 'bandpass';
        bright.frequency.value = 1400;
        bright.Q.value = 0.6;
        const crackGain = context.createGain();
        crackGain.gain.setValueAtTime(0.0001, start);
        crackGain.gain.exponentialRampToValueAtTime(peak * 0.5, start + 0.01);
        crackGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
        crack.connect(bright).connect(crackGain).connect(weather);
        crack.start(start, Math.random());
        crack.stop(start + 0.5);
      },
      playBird: ({delaySec = 0} = {}) => {
        const song =
          BIRD_SONGS[Math.floor(Math.random() * BIRD_SONGS.length)] ?? [];
        for (const [frequency, startSec] of song) {
          chirp({
            frequency,
            gain: 0.03,
            startSec: Math.max(0, delaySec) + startSec,
            durationSec: 0.07,
          });
        }
      },
      setVolume: next => {
        weather.gain.setTargetAtTime(
          volumeGain(next.rain),
          context.currentTime,
          0.05,
        );
        notes.gain.setTargetAtTime(
          volumeGain(next.drops),
          context.currentTime,
          0.05,
        );
      },
      stop: () => {
        if (isStopped) {
          return;
        }
        isStopped = true;
        master.gain.setTargetAtTime(0, context.currentTime, 0.15);
        window.setTimeout(() => {
          rain.stop();
          void context.close();
        }, 800);
      },
    };
  } catch (error) {
    console.debug('[rainSounds] could not start the rain sounds', error);
    return SILENT;
  }
}
